/*
 * tt-quick-actions.js
 * ---------------------------------------------------------------
 * Global handlers for the TT compact "Quick view" and "Quick add"
 * buttons. Lives on every page so any section (tabbed products,
 * search dropdown, future surfaces) can drop in elements with:
 *
 *   - [data-tt-quickadd="<variant_id>"]      → add 1 to cart
 *   - [data-tt-quickview="<product_handle>"] → open quick view modal
 *
 * The quick-view <dialog> lives in layout/theme.liquid so it's
 * always available.
 */
(function () {
  function cartAddSections() {
    /* Always refresh the header bubble + drawer. When we're on the cart page,
       also refresh the on-page line items + footer so a quick-add shows there
       straight away (those sections only exist on /cart). */
    var list = ['cart-drawer', 'cart-icon-bubble'];
    if (document.getElementById('main-cart-items')) list.push('main-cart-items');
    if (document.getElementById('main-cart-footer')) list.push('main-cart-footer');
    return list.join(',');
  }

  function postCartAdd(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: parseInt(variantId, 10), quantity: qty || 1 }],
        /* Ask Shopify to return these section fragments rendered against
           the post-add cart, so the UI refresh needs no second request. */
        sections: cartAddSections(),
        sections_url: window.location.pathname
      })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (j) {
        var err = new Error(j.description || 'Could not add to cart');
        err.status = r.status;
        throw err;
      });
      return r.json();
    });
  }

  /* "Added to basket" toast ----------------------------------------
     One reusable element pinned bottom-centre (orange + black). Fired
     from the quick-add success path, so every quick-add surface on the
     site gets it. Re-firing resets the auto-dismiss timer. */
  var toastEl = null, toastTimer = null;

  function openCart() {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
    var bubble = document.getElementById('cart-icon-bubble');
    var link = bubble && bubble.closest('a');
    if (link) { link.click(); return; }
    window.location.href = '/cart';
  }

  function getToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'tt-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<span class="tt-toast__check" aria-hidden="true">' +
        '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>' +
      '</span>' +
      '<img class="tt-toast__thumb" alt="" width="40" height="40">' +
      '<span class="tt-toast__text">' +
        '<span class="tt-toast__title">Added to basket</span>' +
        '<span class="tt-toast__product"></span>' +
      '</span>' +
      '<button type="button" class="tt-toast__cart" data-tt-toast-cart>View basket</button>';
    document.body.appendChild(toastEl);
    toastEl.querySelector('[data-tt-toast-cart]').addEventListener('click', openCart);
    return toastEl;
  }

  function showAddedToast(response) {
    var item = (response && response.items && response.items[0]) || response || {};
    var name = item.product_title || item.title || 'Item';
    var img = item.image || (item.featured_image && item.featured_image.url);
    var t = getToast();
    t.classList.remove('tt-toast--error');
    t.querySelector('.tt-toast__title').textContent = 'Added to basket';
    t.querySelector('.tt-toast__product').textContent = name;
    var thumb = t.querySelector('.tt-toast__thumb');
    if (img) {
      thumb.src = img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=80';
      thumb.style.display = '';
    } else {
      thumb.removeAttribute('src');
      thumb.style.display = 'none';
    }
    t.classList.remove('is-visible');
    void t.offsetWidth; /* reflow so the pop-in transition re-fires on repeat adds */
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 3000);
  }

  /* Error variant of the same toast — quick-add failures must never
     alert() (raw browser popups feel broken on a storefront). */
  function showErrorToast(message) {
    var t = getToast();
    t.classList.add('tt-toast--error');
    t.querySelector('.tt-toast__title').textContent = 'Could not add to cart';
    t.querySelector('.tt-toast__product').textContent = message || 'Please try again.';
    var thumb = t.querySelector('.tt-toast__thumb');
    thumb.removeAttribute('src');
    thumb.style.display = 'none';
    t.classList.remove('is-visible');
    void t.offsetWidth;
    t.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 4000);
  }

  /* Stock-cap greying ------------------------------------------------
     Card buttons render with data-tt-max="<inventory>" when the variant
     is tracked with no overselling. Once the basket holds every
     available copy (most singles have a quantity of 1) the button greys
     out as "In basket"; removing the item from the basket re-enables it.
     cartQty maps variant id -> quantity currently in the basket. */
  var cartQty = {};

  function applyMaxState() {
    document.querySelectorAll('[data-tt-quickadd][data-tt-max]').forEach(function (btn) {
      var max = parseInt(btn.dataset.ttMax, 10);
      if (isNaN(max) || max <= 0) return; /* sold-out buttons are server-disabled already */
      var maxed = (cartQty[btn.dataset.ttQuickadd] || 0) >= max;
      if (maxed === btn.classList.contains('is-maxed')) return; /* idempotent — safe under MutationObserver */
      var spans = btn.querySelectorAll('span');
      var label = spans.length === 1 ? spans[0] : null;
      if (maxed) {
        btn.classList.add('is-maxed');
        btn.disabled = true;
        if (label) {
          btn.dataset.ttLabelDefault = label.textContent;
          label.textContent = 'In basket';
        }
      } else {
        btn.classList.remove('is-maxed');
        btn.disabled = false;
        if (label && btn.dataset.ttLabelDefault) label.textContent = btn.dataset.ttLabelDefault;
      }
    });
  }

  var cartSyncToken = 0;

  function loadCartQty() {
    var token = ++cartSyncToken; /* stale responses must not clobber newer state */
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (token !== cartSyncToken) return;
        cartQty = {};
        (cart.items || []).forEach(function (item) {
          cartQty[String(item.variant_id)] = item.quantity;
        });
        applyMaxState();
      })
      .catch(function () { /* ignore */ });
  }

  document.addEventListener('DOMContentLoaded', function () {
    loadCartQty();
    /* Dawn publishes cartUpdate on drawer/cart-page quantity changes and
       removals — re-sync so buttons un-grey when an item is taken out. */
    /* (PUB_SUB_EVENTS is a top-level const in constants.js — it's a lexical
       global, not a window property, so test the bare binding.) */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () { loadCartQty(); });
    }
    /* Re-apply to cards added later (infinite scroll, load-more, drawer
       re-renders). applyMaxState makes no DOM writes when nothing changed,
       so the observer doesn't re-trigger itself. */
    var applyTimer = null;
    new MutationObserver(function () {
      clearTimeout(applyTimer);
      applyTimer = setTimeout(applyMaxState, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  });

  var cartUiSeq = 0, cartUiApplied = 0;

  function refreshCartUI(response, seq) {
    /* Two adds of different products can resolve out of order; never let the
       older response's header-bubble/drawer HTML paint over the newer one. */
    if (seq) {
      if (seq < cartUiApplied) return;
      cartUiApplied = seq;
    }
    var sections = response && response.sections;
    var apply = function (html, selector, target) {
      if (!html || !target) return;
      var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      if (fresh) target.innerHTML = fresh.innerHTML;
    };

    if (sections) {
      /* Header count bubble */
      apply(sections['cart-icon-bubble'], '.shopify-section', document.getElementById('cart-icon-bubble'));

      /* Drawer contents — updated quietly in the background, no auto-open.
         (Dawn's drawer.renderContents() would open the drawer, so we swap
         the same elements ourselves and re-bind the overlay click.) */
      var drawer = document.querySelector('cart-drawer');
      var drawerBody = document.querySelector('#CartDrawer');
      if (drawer && drawerBody && sections['cart-drawer']) {
        apply(sections['cart-drawer'], '#CartDrawer', drawerBody);
        drawer.classList.remove('is-empty');
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
      }

      /* Cart page line items + footer (only present on /cart) — keep them in
         sync so a quick-add from the recommendations shows in the basket. */
      var mainItems = document.getElementById('main-cart-items');
      if (mainItems && sections['main-cart-items']) {
        apply(sections['main-cart-items'], '.js-contents', mainItems.querySelector('.js-contents'));
        mainItems.classList.remove('is-empty');
      }
      var mainFooter = document.getElementById('main-cart-footer');
      if (mainFooter && sections['main-cart-footer']) {
        apply(sections['main-cart-footer'], '.js-contents', mainFooter.querySelector('.js-contents'));
        mainFooter.classList.remove('is-empty');
      }
      return;
    }

    /* Fallback: no sections in the response — fetch the bubble alone. */
    var bubble = document.getElementById('cart-icon-bubble');
    if (!bubble) return;
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) { apply(data['cart-icon-bubble'], '.shopify-section', bubble); })
      .catch(function () { /* ignore */ });
  }

  /* Quick add ------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tt-quickadd]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var id = btn.dataset.ttQuickadd;
    if (!id) return;
    btn.disabled = true;
    btn.classList.add('is-loading');
    var uiSeq = ++cartUiSeq;
    postCartAdd(id, 1).then(function (response) {
      btn.classList.remove('is-loading');
      cartQty[id] = (cartQty[id] || 0) + 1;
      refreshCartUI(response, uiSeq);
      showAddedToast(response);
      applyMaxState();
      loadCartQty(); /* authoritative re-sync — covers an add landing before the initial seed */
      /* If that add took the last copy, the button is now greyed as
         "In basket" — skip the green success flash entirely. */
      if (btn.classList.contains('is-maxed')) return;
      btn.classList.add('is-success');
      /* Re-enable straight away — the green flash is only cosmetic. Keeping
         the button locked for the flash silently swallowed rapid repeat adds. */
      btn.disabled = btn.classList.contains('is-maxed');
      setTimeout(function () {
        btn.classList.remove('is-success');
      }, 1200);
    }).catch(function (err) {
      btn.classList.remove('is-loading');
      if (err.status === 422 && btn.dataset.ttMax) {
        /* Stock limit hit (e.g. added on an earlier visit) — grey out
           instead of alerting. */
        cartQty[id] = parseInt(btn.dataset.ttMax, 10);
        applyMaxState();
        btn.disabled = true;
        return;
      }
      btn.disabled = false;
      showErrorToast(err.message);
    });
  });

  /* Quick view modal ------------------------------------------------ */
  function moneyFormat(cents) {
    return (cents / 100).toLocaleString(undefined, {
      style: 'currency',
      currency: window.Shopify && window.Shopify.currency && window.Shopify.currency.active || 'GBP'
    });
  }

  function renderQuickView(modal, content, product) {
    // Resize via Shopify's width query param (robust for any CDN URL; the old
    // "_600x" filename injection produced broken URLs for some images).
    function qvSized(u, w) {
      if (!u) return u;
      return u + (u.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
    }
    var qvImages = (product.images && product.images.length)
      ? product.images
      : (product.featured_image ? [product.featured_image] : []);
    var imgSrc = qvImages.length ? qvSized(qvImages[0], 700) : '';
    // Preload every gallery image up-front so a swipe/arrow never waits on a
    // download — the slide can then start instantly and stays smooth.
    qvImages.forEach(function (u) { var p = new Image(); p.src = qvSized(u, 700); });
    var qvPager = '';
    if (qvImages.length > 1) {
      var arwPrev = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>';
      var arwNext = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>';
      var dots = qvImages.map(function (u, idx) {
        return '<button type="button" class="tt-quickview__dot' + (idx === 0 ? ' is-active' : '') +
          '" data-tt-qv-dot="' + idx + '" aria-label="Image ' + (idx + 1) + '"></button>';
      }).join('');
      qvPager =
        '<div class="tt-quickview__pager">' +
          '<button type="button" class="tt-quickview__pager-arw" data-tt-qv-prev aria-label="Previous image">' + arwPrev + '</button>' +
          '<span class="tt-quickview__dots">' + dots + '</span>' +
          '<button type="button" class="tt-quickview__pager-arw" data-tt-qv-next aria-label="Next image">' + arwNext + '</button>' +
        '</div>';
    }
    var firstAvail = null;
    for (var vi = 0; vi < product.variants.length; vi++) {
      if (product.variants[vi].available) { firstAvail = product.variants[vi]; break; }
    }
    var variantOptions = product.variants.map(function (v) {
      return '<option value="' + v.id + '"' + (v.available ? '' : ' disabled')
        + (firstAvail && v.id === firstAvail.id ? ' selected' : '') + '>'
        + (v.title === 'Default Title' ? product.title : v.title)
        + ' — ' + moneyFormat(v.price)
        + (v.available ? '' : ' (sold out)') + '</option>';
    }).join('');

    content.innerHTML =
      '<div class="tt-quickview__media">' +
        '<div class="tt-quickview__stage" data-tt-qv-stage>' +
          (imgSrc ? '<img class="tt-quickview__main-img" data-tt-qv-main src="' + imgSrc + '" alt="' + product.title + '">' : '') +
          qvPager +
        '</div>' +
      '</div>' +
      '<div class="tt-quickview__details">' +
        '<h2 class="tt-quickview__title" id="QuickviewTitle"><a class="tt-quickview__title-link" href="/products/' + product.handle + '">' + product.title + '</a></h2>' +
        '<p class="tt-quickview__price" data-tt-qv-price>' + moneyFormat(product.price) + '</p>' +
        (product.variants.length > 1
          ? '<label class="tt-quickview__variant-label">Condition / variant</label>' +
            '<select class="tt-quickview__variant" data-tt-qv-variant>' + variantOptions + '</select>'
          : '<input type="hidden" data-tt-qv-variant value="' + product.variants[0].id + '">'
        ) +
        (firstAvail
          ? '<button type="button" class="button button--primary tt-quickview__add" data-tt-qv-add>Add to cart</button>'
          : '<button type="button" class="button button--primary tt-quickview__add" data-tt-qv-add disabled>Sold out</button>') +
        '<a href="/products/' + product.handle + '" class="tt-quickview__details-link">View full product page →</a>' +
      '</div>';

    // Image browse: prev/next arrows (desktop) + swipe (mobile), smooth cross-slide
    var qvMain = content.querySelector('[data-tt-qv-main]');
    var qvStage = content.querySelector('[data-tt-qv-stage]');
    var qvDots = Array.prototype.slice.call(content.querySelectorAll('[data-tt-qv-dot]'));
    var qvReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var qvAnimating = false;
    var qvIndex = 0;
    // If a resized URL ever fails, fall back to the original (load robustness).
    if (qvMain) qvMain.addEventListener('error', function () {
      var raw = qvImages[qvIndex];
      if (raw && qvMain.src !== raw) qvMain.src = raw;
    });
    function qvShow(idx, dir) {
      if (!qvMain || idx === qvIndex || idx < 0 || idx >= qvImages.length) return;
      var full = qvSized(qvImages[idx], 700);
      function settle() {
        qvIndex = idx;
        qvDots.forEach(function (d, k) { d.classList.toggle('is-active', k === idx); });
      }
      if (qvReduce || qvAnimating || !qvStage) { qvMain.src = full; settle(); return; }
      qvAnimating = true;
      var pre = new Image(); var started = false;
      function run() {
        if (started) return; started = true;
        var DUR = 340, EASE = 'cubic-bezier(0.33, 1, 0.68, 1)'; // easeOutCubic
        var outX = (dir > 0 ? '-100%' : '100%');
        var inc = qvMain.cloneNode(false);
        inc.removeAttribute('data-tt-qv-main');
        inc.src = full;
        inc.style.position = 'absolute'; inc.style.inset = '0';
        inc.style.transition = 'none';
        inc.style.willChange = 'transform';
        inc.style.transform = 'translateX(' + (dir > 0 ? '100%' : '-100%') + ')';
        qvMain.style.transition = 'none';
        qvMain.style.willChange = 'transform';
        qvStage.appendChild(inc);
        // Double rAF: paint the start state first, then animate — smoother and
        // more reliable than a forced reflow.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            qvMain.style.transition = 'transform ' + DUR + 'ms ' + EASE;
            inc.style.transition = 'transform ' + DUR + 'ms ' + EASE;
            qvMain.style.transform = 'translateX(' + outX + ')';
            inc.style.transform = 'translateX(0)';
          });
        });
        window.setTimeout(function () {
          qvMain.src = full;
          qvMain.style.transition = 'none'; qvMain.style.transform = ''; qvMain.style.willChange = '';
          if (inc.parentNode) inc.parentNode.removeChild(inc);
          settle();
          qvAnimating = false;
        }, DUR + 30);
      }
      pre.onload = run; pre.onerror = run; pre.src = full;
      if (pre.complete) run();
    }
    function qvStep(dir) {
      var ni = qvIndex + dir;
      if (ni < 0) ni = qvImages.length - 1;
      if (ni >= qvImages.length) ni = 0;
      qvShow(ni, dir);
    }
    var qvPrev = content.querySelector('[data-tt-qv-prev]');
    var qvNext = content.querySelector('[data-tt-qv-next]');
    if (qvPrev) qvPrev.addEventListener('click', function () { qvStep(-1); });
    if (qvNext) qvNext.addEventListener('click', function () { qvStep(1); });
    qvDots.forEach(function (d) {
      d.addEventListener('click', function () {
        var k = parseInt(d.getAttribute('data-tt-qv-dot'), 10);
        if (k !== qvIndex) qvShow(k, k > qvIndex ? 1 : -1);
      });
    });
    if (qvStage && qvImages.length > 1) {
      var qvSx = 0, qvSy = 0;
      qvStage.addEventListener('touchstart', function (e) {
        var t = e.changedTouches[0]; qvSx = t.clientX; qvSy = t.clientY;
      }, { passive: true });
      qvStage.addEventListener('touchend', function (e) {
        var t = e.changedTouches[0]; var dx = t.clientX - qvSx, dy = t.clientY - qvSy;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) qvStep(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    var select = content.querySelector('[data-tt-qv-variant]');
    var priceEl = content.querySelector('[data-tt-qv-price]');
    if (select && select.tagName === 'SELECT' && priceEl) {
      select.addEventListener('change', function () {
        var v = product.variants.find(function (x) { return String(x.id) === select.value; });
        if (v) priceEl.textContent = moneyFormat(v.price);
      });
    }

    var addBtn = content.querySelector('[data-tt-qv-add]');
    addBtn.addEventListener('click', function () {
      var vid = select ? select.value : null;
      if (!vid) {
        var hidden = content.querySelector('input[data-tt-qv-variant]');
        if (hidden) vid = hidden.getAttribute('value');
      }
      if (!vid) return;
      addBtn.disabled = true;
      addBtn.textContent = 'Adding…';
      var uiSeq = ++cartUiSeq;
      postCartAdd(vid, 1).then(function (response) {
        addBtn.textContent = 'Added ✓';
        cartQty[vid] = (cartQty[vid] || 0) + 1;
        refreshCartUI(response, uiSeq);
        applyMaxState();
        loadCartQty();
        setTimeout(function () { modal.close(); }, 700);
      }).catch(function (err) {
        if (err.status === 422) {
          /* Nothing left to add — the basket already holds every copy
             (or it just sold out). Grey out like the grid buttons do. */
          addBtn.disabled = true;
          addBtn.textContent = 'In basket';
          loadCartQty(); /* authoritative re-sync greys the grid button too */
          return;
        }
        addBtn.disabled = false;
        addBtn.textContent = 'Couldn’t add — try again';
        setTimeout(function () { addBtn.textContent = 'Add to cart'; }, 2500);
      });
    });
  }

  var qvGen = 0; /* bumped per open/close so a slow fetch can't paint a stale product */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-tt-quickview]');
    if (!btn) return;
    var modal = document.querySelector('[data-tt-quickview-modal]');
    if (!modal) return;
    e.preventDefault();
    var gen = ++qvGen;
    var content = modal.querySelector('[data-tt-quickview-content]');
    var handle = btn.dataset.ttQuickview;
    content.innerHTML = '<div class="tt-quickview__loading">Loading…</div>';
    if (typeof modal.showModal === 'function') modal.showModal(); else modal.setAttribute('open', '');
    /* Lock page scroll behind the dialog; restored on the close listener. */
    document.body.dataset.ttQvPrevOverflow = document.body.style.overflow || '';
    document.body.style.overflow = 'hidden';
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        if (gen !== qvGen) return; /* another card opened / modal closed since */
        renderQuickView(modal, content, product);
      })
      .catch(function () {
        if (gen !== qvGen) return;
        content.innerHTML = '<p>Could not load product. <a href="/products/' + handle + '">View full page →</a></p>';
      });
  });

  /* Wire up close + backdrop click on the global modal once it's parsed. */
  document.addEventListener('DOMContentLoaded', function () {
    var modal = document.querySelector('[data-tt-quickview-modal]');
    if (!modal) return;
    var closeBtn = modal.querySelector('[data-tt-quickview-close]');
    modal.addEventListener('click', function (e) {
      if (e.target === modal) modal.close();
    });
    /* Any close (button, backdrop, Escape) invalidates in-flight fetches
       and restores page scroll. */
    modal.addEventListener('close', function () {
      qvGen++;
      document.body.style.overflow = document.body.dataset.ttQvPrevOverflow || '';
      delete document.body.dataset.ttQvPrevOverflow;
    });
    if (closeBtn) closeBtn.addEventListener('click', function () { modal.close(); });
  });
})();
